import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { OrderModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";
import ADIRfqPopupCartNotificationModal from "./ADIRfqPopupCartNotificationModal";
import ADIRfqPopupNonReturnableModal from "./ADIRfqPopupNonReturnableModal";
import ADILogicBrokerModal from "./ADILogicBrokerModal";

interface OwnProps {
    canUserAccess: boolean;
    canUserAcceptAndCheckout: boolean;
    formattedDate: string;
    quoteDetails?: OrderModel;
    formattedExpDate: string;
    isCartNotificationModalOpen: boolean;
    closeModalCartNotification: () => void;
    isNonReturnableModalOpen: boolean;
    closeModalNonReturnable: () => void;
    isLogicBrokerModalOpen: boolean;
    closeModalLogicBroker: () => void;
    acceptCheckout: (url: string, customerOrderId: string) => void;
    checkNonReturnableProducts: (url: string, customerOrderId: string) => void;
    AgreeAndContinueCheckout: (url: string, customerOrderId: string) => void;
    isAcceptCheckoutDisable: boolean;
    isExpireQuoteWarning: (dateVal: any) => boolean;
    isQuoteExpired: (dateVal: any) => boolean;
}

type Props = OwnProps;

export interface ADIRfqQuoteDetailsHeaderSectionStyles {
    quoteDetailsHeaderSection?: InjectableCss;
    quoteDetailsRow?: InjectableCss;
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    valueText?: TypographyProps;
    acceptAndCheckoutButton?: ButtonPresentationProps;
}

export const adiRfqQuoteDetailsHeaderSectionStyles: ADIRfqQuoteDetailsHeaderSectionStyles = {
    quoteDetailsHeaderSection: {
        css: css`
            width: 100%;
            padding-top: 19px;
            padding-bottom: 15px;
            display: block;
            @media only screen and (max-width: 1024px) {
                display: none;
            }
        `,
    },
    quoteDetailsRow: {
        css: css`
            justify-content: space-between;
            display: flex;
            flex-direction: row;
            flex-grow: 0;
            flex-wrap: nowrap;
            width: 100%;
            background-color: ${getColor("common.background")};
            @media print {
                flex-direction: initial;
                flex-wrap: wrap;
            }
        `,
    },
    wrapper: {
        css: css`
            padding: 0 29px;
            border-left: 2px solid ${ADIColors.white1};
            display: flex;
            flex-flow: row nowrap;
            flex-direction: column;
            flex-grow: 0;
            word-break: break-word;
            overflow: hidden;
            text-overflow: ellipsis;
            min-width: 0;
            justify-content: flex-start;
            font-size: 16px;
            font-weight: bold;
            line-height: 18px;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            white-space: normal;
            position: relative;
            margin-right: 0;
            margin-left: auto;
            &:first-child {
                padding-left: 0;
                border-left: none;
                margin-left: 0;
                margin-right: 0;
            }
            &.rd-header-expdate-column {
                .column-details-error {
                    width: auto;
                    height: 13px;
                    margin-left: 8px;
                    vertical-align: top;
                    position: relative;
                    top: 3px;
                }
            }
            &.rd-header-mtotal-column {
                padding-left: 29px;
                padding-right: 0;
                align-items: flex-end;
                @media print {
                    padding-left: 0px;
                }
            }
            &.rd-header-item-column {
                align-items: center;
            }
            &.rd-header-checkout-column {
                padding-right: 0px;
                border-left: none;
                margin-left: 0px;
                margin-right: 0px;
            }
            @media print {
                margin-left: 0px;
                border-left: 0px;
                padding: 0px;
                flex-direction: row;
                align-items: flex-start;
                width: 50%;
            }
        `,
    },
    titleText: {
        variant: "p",
        css: css`
            font-size: 16px;
            font-weight: bold;
            line-height: 18px;
            @media print {
                font-size: 14px;
                color: ${ADIColors.black};
                width: 35%;
                text-align: left;
            }
            margin-bottom: 4px;
        `,
    },
    valueText: {
        css: css`
            font-size: 14px;
            text-transform: capitalize;
            font-weight: normal;
            line-height: 20px;
            color: ${getColor("text.main")};
            display: flex;
            &.rd-header-mtotal-value {
                font-weight: bold;
            }
            @media print {
                font-size: 13px;
                color: ${ADIColors.black};
                padding-left: 15px;
            }
        `,
    },
    acceptAndCheckoutButton: {
        css: css`
            width: 240px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            color: ${getColor("common.background")};
            border-radius: 4px;
            padding-top: 10px;
            padding-bottom: 10px;
            margin-bottom: 0;
            &:disabled {
                background: ${ADIColors.blue8};
                border-color: transparent;
            }
        `,
    },
};

const styles = adiRfqQuoteDetailsHeaderSectionStyles;

let isExpireQuoteWarningFlag = false;
let isQuoteExpiredFlag = false;

const ADIRfqQuoteDetailsHeaderSection: FC<Props> = ({
    canUserAccess,
    canUserAcceptAndCheckout,
    formattedDate,
    quoteDetails,
    formattedExpDate,
    isCartNotificationModalOpen,
    closeModalCartNotification,
    isNonReturnableModalOpen,
    closeModalNonReturnable,
    isLogicBrokerModalOpen,
    closeModalLogicBroker,
    acceptCheckout,
    checkNonReturnableProducts,
    AgreeAndContinueCheckout,
    isAcceptCheckoutDisable,
    isExpireQuoteWarning,
    isQuoteExpired,
}) => {
    const currentDate = new Date();

    // const quoteExpired = (expiryDate: any) => {
    //     const expiry = new Date(expiryDate);
    //     expiry.setHours(0, 0, 0, 0);
    //     currentDate.setHours(0, 0, 0, 0);

    //     if (expiry < currentDate) return true;
    //     else return false;
    // };

    const formatDate = (dateString: any) => {
        if (!dateString) {
            return "";
        }
        const newDate = new Date(dateString);
        const splitedDate = newDate.toString().split(" ");
        let finalDate = "";
        if (splitedDate) {
            finalDate = `${splitedDate[1]}. ${splitedDate[2]}, ${splitedDate[3]}`;
        }
        return finalDate;
    };

    const customerPO = quoteDetails?.customerPO;
    const referenceNO = quoteDetails?.orderRefNum;
    const orderLinesLength = quoteDetails?.orderLines?.length;
    const expiryDate = quoteDetails?.properties.expiryDate;
    const projectStatus = quoteDetails?.properties["projectStatus"];
    isExpireQuoteWarningFlag = isExpireQuoteWarning(expiryDate);
    // isQuoteExpiredFlag = quoteExpired(expiryDate); ---- P21 Change
    isQuoteExpiredFlag = isQuoteExpired(expiryDate);

    const poTextStyle = () => css`
        color: ${customerPO === "" ? ADIColors.gray3 : getColor("text.main")};
        margin-bottom: 5px;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.13;
        @media print {
            font-size: 14px;
            color: ${ADIColors.black};
        }
    `;

    const poNumberStyle = () => css`
        color: ${customerPO === "" ? ADIColors.gray3 : getColor("text.main")};
        font-size: 14px;
        text-transform: capitalize;
        line-height: 1.43;
        font-weight: normal;
        @media print {
            font-size: 13px;
            color: ${ADIColors.black};
            padding-left: 105px;
        }
    `;

    const referenceTextStyle = () => css`
        color: ${referenceNO === "" ? ADIColors.gray3 : getColor("text.main")};
        @media print {
            font-size: 14px;
            color: ${ADIColors.black};
        }
        margin-bottom: 5px;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.13;
    `;

    const referenceNumberStyle = () => css`
        color: ${referenceNO === "" ? ADIColors.gray3 : getColor("text.main")};
        font-size: 14px;
        text-transform: capitalize;
        line-height: 1.43;
        font-weight: normal;
        @media print {
            font-size: 13px;
            color: ${ADIColors.black};
            padding-left: 57px;
        }
    `;
    const filteredLbWithNonZeroQty = quoteDetails?.orderLines?.filter(
        item => item?.properties?.logicBroker?.toLowerCase() === "yes" && Number(item.qtyOrdered) !== 0,
    );
    const disabledButton =
        quoteDetails?.properties["isZeroPriceQuote"] === "Y" ||
        orderLinesLength === 0 ||
        quoteDetails?.invoiceOrderLines[0]?.orderTotal === 0 ||
        canUserAccess ||
        canUserAcceptAndCheckout === false ||
        // quoteExpired(expiryDate) || ---P21 Change
        isQuoteExpired(expiryDate) ||
        isAcceptCheckoutDisable ||
        formattedExpDate === "Invalid. Date";

    return (
        <StyledWrapper {...styles.quoteDetailsHeaderSection}>
            <StyledWrapper {...styles.quoteDetailsRow}>
                <StyledWrapper className="rd-header-date-column" {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Date")}</Typography>
                    <Typography {...styles.valueText}>{formattedDate}</Typography>
                </StyledWrapper>
                {projectStatus === "Y" && (
                    <StyledWrapper className="rd-header-project-column" {...styles.wrapper}>
                        <Typography {...styles.titleText}>{translate("Project Name")}</Typography>
                        <Typography {...styles.valueText}>{quoteDetails?.properties["projectName"]}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper className="rd-header-po-column" {...styles.wrapper}>
                    <Typography {...styles.titleText} css={poTextStyle()}>
                        {translate("PO #")}
                    </Typography>
                    <Typography {...styles.valueText} css={poNumberStyle()}>
                        {customerPO === "" ? translate("N/A") : customerPO?.toLowerCase()}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper className="rd-header-ref-column" {...styles.wrapper}>
                    <Typography {...styles.titleText} css={referenceTextStyle()}>
                        {translate("Reference #")}
                    </Typography>
                    <Typography {...styles.valueText} css={referenceNumberStyle()}>
                        {referenceNO === "" ? translate("N/A") : referenceNO?.toLowerCase()}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper className="rd-header-expdate-column" {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Exp. Date")}</Typography>
                    <Typography {...styles.valueText}>
                        {formattedExpDate === "Invalid. Date" ? "" : formattedExpDate}
                        {(isExpireQuoteWarningFlag || isQuoteExpiredFlag) && !(orderLinesLength === 0) && (
                            <Typography as="span">
                                <img
                                    alt="column-details-error"
                                    className="column-details-error"
                                    src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Quotelist/icon-warning.svg"
                                />
                            </Typography>
                        )}
                    </Typography>
                </StyledWrapper>
                {quoteDetails?.requestor && (
                    <StyledWrapper className="rd-header-po-column" {...styles.wrapper}>
                        <Typography {...styles.titleText}>{translate("Requestor")}</Typography>
                        <Typography {...styles.valueText}>{quoteDetails?.requestor}</Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper className="rd-header-item-column" {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Items")}</Typography>
                    <Typography {...styles.valueText}>
                        {quoteDetails?.invoiceOrderLines[0]?.invoiceOrderLines.length}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper className="rd-header-mtotal-column" {...styles.wrapper}>
                    <Typography {...styles.titleText}>{translate("Material Total")}</Typography>
                    <Typography {...styles.valueText} className="rd-header-mtotal-value">
                        {quoteDetails?.invoiceOrderLines[0]?.amountDisplay}
                    </Typography>
                </StyledWrapper>
                {!(projectStatus === "Y") && (
                    <StyledWrapper className="rd-header-checkout-column" {...styles.wrapper}>
                        <Button
                            disabled={disabledButton}
                            {...styles.acceptAndCheckoutButton}
                            onClick={() =>
                                acceptCheckout(
                                    "/Checkout/ReviewAndPay?cartId=",
                                    `${quoteDetails?.insiteCustomerOrderId}`,
                                )
                            }
                        >
                            {translate("Accept & Checkout")}
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <ADIRfqPopupNonReturnableModal
                quoteDetails={quoteDetails}
                isNonReturnableModalOpen={isNonReturnableModalOpen}
                closeModalNonReturnable={closeModalNonReturnable}
                AgreeAndContinueCheckout={AgreeAndContinueCheckout}
            />
            {filteredLbWithNonZeroQty && filteredLbWithNonZeroQty.length > 0 && (
                <ADILogicBrokerModal
                    quoteDetails={quoteDetails}
                    isLogicBrokerModalOpen={isLogicBrokerModalOpen}
                    closeModalLogicBroker={closeModalLogicBroker}
                    AgreeAndContinueCheckout={AgreeAndContinueCheckout}
                />
            )}

            <ADIRfqPopupCartNotificationModal
                isCartNotificationModalOpen={isCartNotificationModalOpen}
                closeModalCartNotification={closeModalCartNotification}
                quoteDetails={quoteDetails}
                checkNonReturnableProducts={checkNonReturnableProducts}
            />
        </StyledWrapper>
    );
};

export default ADIRfqQuoteDetailsHeaderSection;
